"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/app/_module/components/ui/card";
import { useAgencyLiveAgents } from "../../hooks/use-agency-routes";
import MultiAgentMap from "./MultiAgentMap";
import AgencyRoutesHistory from "./AgencyRoutesHistory";
import type { LiveAgent } from "../../types/routes";

interface AgencyRoutesTabProps {
  agencyId: string;
}

type SubTab = "live" | "history";

function statusDotColor(statusLabel: string): string {
  switch (statusLabel) {
    case "Knocking":
      return "bg-emerald-500";
    case "Between stops":
      return "bg-blue-500";
    case "Starting":
      return "bg-amber-500";
    default:
      return "bg-gray-400";
  }
}

function LiveView({ agencyId }: { agencyId: string }) {
  const { data, isLoading, dataUpdatedAt } = useAgencyLiveAgents(agencyId);
  const [now, setNow] = useState<number>(Date.now());
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const agents: LiveAgent[] = useMemo(() => data ?? [], [data]);
  const activeCount = agents.length;
  const secondsAgo = dataUpdatedAt
    ? Math.max(0, Math.floor((now - dataUpdatedAt) / 1000))
    : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            <span className="text-xs font-semibold text-red-600 uppercase tracking-wide">
              Live
            </span>
          </div>
          <span className="text-gray-300">|</span>
          <span className="text-sm text-gray-700">
            <span className="font-semibold text-gray-900">{activeCount}</span>{" "}
            {activeCount === 1 ? "agent" : "agents"} active
          </span>
        </div>
        <div className="text-xs text-gray-400">
          {dataUpdatedAt ? `Updated ${secondsAgo}s ago` : "—"}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[500px] rounded-xl border border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      ) : activeCount === 0 ? (
        <div className="flex items-center justify-center h-[500px] rounded-xl border border-gray-200 bg-gray-50 text-center px-6">
          <p className="text-sm text-gray-500 max-w-md">
            No agents are actively routing right now. Agents appear here while
            checked in to a door-knocking route.
          </p>
        </div>
      ) : (
        <>
          <MultiAgentMap
            agents={agents}
            selectedAgentId={selectedAgentId}
            onSelectAgent={setSelectedAgentId}
            height="500px"
          />

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Agent</th>
                  <th className="text-left font-medium px-4 py-2.5">Status</th>
                  <th className="text-left font-medium px-4 py-2.5">
                    Last Stop
                  </th>
                  <th className="text-left font-medium px-4 py-2.5">
                    Doors Today
                  </th>
                  <th className="text-left font-medium px-4 py-2.5">
                    Next Stop
                  </th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => {
                  const isSelected = selectedAgentId === agent.userId;
                  return (
                    <tr
                      key={agent.userId}
                      onClick={() => setSelectedAgentId(agent.userId)}
                      className={`border-t border-gray-100 cursor-pointer transition-colors ${
                        isSelected ? "bg-emerald-50/40" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {agent.name}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${statusDotColor(
                              agent.statusLabel,
                            )}`}
                          />
                          <span className="text-gray-700">
                            {agent.statusLabel}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {agent.currentStopIndex != null &&
                        agent.currentStopIndex >= 0
                          ? `Stop ${agent.currentStopIndex + 1} of ${
                              agent.totalStops ?? "—"
                            }`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {agent.doorsKnockedToday ?? 0}
                      </td>
                      <td className="px-4 py-3 text-gray-600 truncate max-w-[240px]">
                        {agent.nextStopAddress || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function AgencyRoutesTab({ agencyId }: AgencyRoutesTabProps) {
  const [subTab, setSubTab] = useState<SubTab>("live");

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardHeader className="pb-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          <button
            type="button"
            onClick={() => setSubTab("live")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              subTab === "live"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Live
          </button>
          <button
            type="button"
            onClick={() => setSubTab("history")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              subTab === "history"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            History
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {subTab === "live" ? (
          <LiveView agencyId={agencyId} />
        ) : (
          <AgencyRoutesHistory agencyId={agencyId} />
        )}
      </CardContent>
    </Card>
  );
}
